import { useEffect, useMemo } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType, object, ObjectSchema, string } from 'yup';
import { toast } from 'react-hot-toast';

import {
  Button,
  FormField,
  FormSubmitButton,
  GridCol,
  GridRow,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  Select,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';
import { Role, usePutPersonsTenantUserInviteMutation } from '@client/api';

type FormModel = {
  email: string;
  role: string;
};

const schema: ObjectSchema<FormModel> = object({
  email: string().email().required(),
  role: string().oneOf(['ClientAdmin', 'ClientUser']).required(),
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormModel>;
type FormDefaultValues = DeepPartial<FormValues>;

interface ProfileUserInviteModalProps {
  tenantId?: string;
  currentUserRole?: string | null;
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

export function ProfileUserInviteModal({ tenantId, currentUserRole, open, onClose, onSubmitted }: ProfileUserInviteModalProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const [putPersonsTenantUserInvite, { isLoading }] = usePutPersonsTenantUserInviteMutation();

  // Determine which roles the current user can invite
  // ClientAdmin can invite ClientAdmin and ClientUser
  // ClientUser can only invite ClientUser
  const canInviteAdmin = currentUserRole === 'ClientAdmin' || currentUserRole === 'Creator' || currentUserRole === 'Owner';
  
  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      email: '',
      role: canInviteAdmin ? 'ClientAdmin' : 'ClientUser',
    }),
    [canInviteAdmin],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { handleSubmit, reset } = methods;

  const handleFormSubmit = async (data: FormValues) => {
    await putPersonsTenantUserInvite({ body: { email: data.email, role: data.role as Role } })
      .unwrap()
      .then(() => {
        toast.success(tGlobal('user.userInvitedSuccessfully'));
        onSubmitted();
      })
      .catch((error) => {});
  };

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, open, reset]);

  return (
    <Modal open={open} size="lg" onClose={onClose} title={tCommon('inviteUserToTenant')}>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalContent className="space-y-8 p-4">
            <GridRow cols={{ def: 1, sm: 6 }} spacingX={2} spacingY={4}>
              <GridCol span={{ def: 1, sm: 6 }}>
                <FormField<FormValues, FormPath>
                  name="email"
                  label={tGlobal('user.email')}
                  required
                  render={({ field }) => <Input {...field} />}
                />
              </GridCol>

              <GridCol span={{ def: 1, sm: 6 }}>
                <FormField<FormValues, FormPath>
                  name="role"
                  label={tGlobal('user.role')}
                  required
                  render={({ field }) => (
                    <Select {...field}>
                      {canInviteAdmin && (
                        <option value="ClientAdmin">{tType('UserRole.ClientAdmin')}</option>
                      )}
                      <option value="ClientUser">{tType('UserRole.ClientUser')}</option>
                    </Select>
                  )}
                />
              </GridCol>
            </GridRow>
          </ModalContent>
          <ModalFooter>
            <Button color="primary-dark" onClick={onClose}>
              {tCommon('back')}
            </Button>
            <FormSubmitButton type="submit" progressing={isLoading}>
              {tCommon('send')}
            </FormSubmitButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
}
